// ===========================================
// Accordion (FAQ) Block
// Native <details> elements, enhanced with deep links: each question carries an id, so
// "#question-slug" opens and scrolls to it, and opening a question puts its link in the
// address bar ready to share.
// ===========================================

const ITEM_SELECTOR = '[data-accordion-item]';

export function initAccordions(): void {
  const items = document.querySelectorAll<HTMLDetailsElement>(ITEM_SELECTOR);
  if (items.length === 0) return;

  items.forEach((item) => {
    item.addEventListener('toggle', () => syncHashWithItem(item));
  });

  // Same-page links to a question, and back/forward between them.
  window.addEventListener('hashchange', () => openFromHash(true));

  openFromHash(false);
}

/**
 * Opens the question the URL points at and brings it into view. The id may be the question
 * itself or something inside its answer (a heading in the rich text, say) — either way the
 * enclosing question has to be open for the target to be visible.
 */
function openFromHash(smooth: boolean): void {
  const id = idFromHash(window.location.hash);
  if (!id) return;

  const target = document.getElementById(id);
  const item = target?.closest<HTMLDetailsElement>(ITEM_SELECTOR);
  if (!target || !item) return;

  item.open = true;

  // The browser's own fragment jump can't reach content inside a closed question, and on
  // first load it runs before layout settles, so scroll explicitly once it's open. The
  // window is scrolled directly rather than via scrollIntoView, which would scroll any
  // overflow-clipping ancestor instead of the page.
  //
  // 'instant' rather than 'auto' on first load: base.scss sets scroll-behavior: smooth on
  // html, and 'auto' defers to that, so a shared link would glide down from the top of the
  // page instead of opening at the question.
  requestAnimationFrame(() => {
    const offset = parseFloat(getComputedStyle(item).scrollMarginTop) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: smooth ? 'smooth' : 'instant' });
  });
}

/**
 * Keeps the address bar pointing at the open question. replaceState rather than setting
 * location.hash, so opening questions neither jumps the page nor fills the history.
 */
function syncHashWithItem(item: HTMLDetailsElement): void {
  const currentId = idFromHash(window.location.hash);
  const currentTarget = currentId ? document.getElementById(currentId) : null;
  const hashPointsInsideItem = currentTarget !== null && item.contains(currentTarget);

  if (item.open) {
    // Leave a link to something inside this answer alone rather than widening it.
    if (!hashPointsInsideItem) {
      history.replaceState(history.state, '', `#${encodeURIComponent(item.id)}`);
    }
  } else if (hashPointsInsideItem) {
    // Closing the linked question shouldn't leave a link to it behind.
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
  }
}

function idFromHash(hash: string): string | null {
  if (hash.length < 2) return null;

  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
}
